/**
 * Acquisition-source + referral-token capture that survives session history.
 *
 * Shopify's landing_site records only the FIRST URL of a browser session, so
 * a returning visitor who arrives via a tagged link (Chrome extension banner,
 * ChatGPT citation, email campaign, referral link) loses attribution. This
 * script reads utm_* and ?ref= off the CURRENT page URL on every load,
 * stores the latest values (last-touch, 30-day window), and mirrors them
 * into hidden cart attributes so the orders webhook can act on them
 * regardless of session age. Underscore-prefixed attribute names keep them
 * out of customer-facing cart/checkout summaries (same convention as
 * "_Custom Amount").
 *
 *   _Galore Attribution — JSON {source, medium, campaign, landing, touched_at}
 *   _Galore Referral    — a 32-hex referral token OR an 8-char referral code
 *                         (validated server-side; ?ref= accepts both; a later
 *                         referral click or a code typed into the cart field
 *                         replaces an earlier unconverted one, per the
 *                         referral spec)
 *   _Galore Device      — 32-hex first-party device identifier for Visa
 *                         CE3.0 dispute evidence (near-auto-win when a
 *                         disputed order matches 2+ prior undisputed orders
 *                         120-365d old on 2 of {device ID, IP, account ID,
 *                         shipping address}; digital gift cards have no
 *                         shipping address, so this is the second element
 *                         beside the customer account). Purely a random
 *                         CSPRNG token, no fingerprinting, no third parties;
 *                         minted only when a cart is being written (not on
 *                         browse-only views). Stored on verified_customers.
 *
 *                         DURABILITY IS ENGINE-DEPENDENT, not guaranteed:
 *                         localStorage + a 400-day cookie that restore each
 *                         other survive the 120d CE3.0 window on Chrome/Edge/
 *                         Firefox (desktop + Android) — but WebKit (Safari,
 *                         and ALL iOS/iPadOS browsers) caps script-set cookies
 *                         at 7 days and evicts localStorage after 7 days of
 *                         no interaction, so the id rotates and yields NO
 *                         qualifying evidence for those buyers. A durable
 *                         cross-engine version needs a server Set-Cookie via
 *                         a Shopify App Proxy (not ITP-capped). Also bypassed
 *                         entirely by accelerated/Shop Pay/in-app checkout.
 *
 * Server counterparts in galore-rewards-api: attribution_service.py and the
 * referral programme (docs/referral-program-spec.md). A browse-only visitor
 * with no attribution/referral and no existing cart makes zero extra requests
 * (and is not given a device id); the /cart round-trip fires only when there
 * is something to write or a cart already exists.
 */
(function () {
  // Evaluated once per document: a second evaluation would add another
  // cartUpdate subscriber with its own in-flight state and double the writes.
  if (window.GaloreAttribution) return;
  var ATTR_KEY = "galoreAttribution";
  var REF_KEY = "galoreReferral";
  var SYNC_KEY = "galoreCartSyncSignature";
  var DEVICE_KEY = "galoreDeviceId";
  var DEVICE_COOKIE = "galore_did";
  var DEVICE_RE = /^[a-f0-9]{32}$/;
  var MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;
  var TOKEN_RE = /^[a-f0-9]{32}$/;
  var CODE_RE = /^[A-HJ-NP-Z2-9]{8}$/;
  var memoryReferral = null;   // only when storage is blocked: carries the referral within the page
  var pendingClear = false;    // clearReferral() asked for an explicit "" write

  // Lowercase hex token, uppercase code, or null. A pasted link or query
  // string containing ref= is unwrapped first so "the whole link" works in
  // the cart field.
  function normaliseReferral(raw) {
    if (raw === null || raw === undefined) return null;
    var s = String(raw).trim();
    var m = s.match(/[?&]ref=([^&#\s]+)/i);
    if (m) {
      try { s = decodeURIComponent(m[1]); } catch (e) { return null; }
    }
    if (TOKEN_RE.test(s.toLowerCase())) return s.toLowerCase();
    var c = s.replace(/[\s\-]/g, "").toUpperCase();
    return CODE_RE.test(c) ? c : null;
  }

  // The in-memory copy is used ONLY when storage is blocked. If storage works
  // it stays null, so a referral cleared in another tab (or expired by
  // readFresh) is not resurrected by this tab's next cartUpdate sync.
  function storeReferral(value) {
    memoryReferral = null;
    try {
      localStorage.setItem(REF_KEY, JSON.stringify({ token: value, ts: Date.now() }));
      localStorage.removeItem(SYNC_KEY);
    } catch (e) { memoryReferral = value; }
  }

  // Clears the referral only. SYNC_KEY stays: dropping the referral already
  // changes the write signature, and removing SYNC_KEY would make a second
  // clearReferral() rewrite the cart for no reason.
  function forgetReferral() {
    memoryReferral = null;
    try { localStorage.removeItem(REF_KEY); } catch (e) { /* best-effort */ }
  }

  function readFresh(key) {
    try {
      var raw = localStorage.getItem(key);
      if (!raw) return null;
      var saved = JSON.parse(raw);
      if (!saved || !saved.ts || Date.now() - saved.ts > MAX_AGE_MS) {
        localStorage.removeItem(key);
        return null;
      }
      return saved;
    } catch (e) {
      return null;
    }
  }

  // The cart field re-renders from this after a CONFIRMED referral write
  // (set or cleared) so it never keeps showing a value the cart no longer
  // holds: a ?ref= landing, a cartUpdate re-sync or a drawer rebuilt from an
  // earlier cart response can all otherwise leave it stale.
  function announceReferral(value) {
    try {
      document.dispatchEvent(new CustomEvent("galore:referral", { detail: { referral: value || "" } }));
    } catch (e) { /* never break the storefront */ }
  }

  // A lost response is not a failed write: Shopify may have applied the
  // update. Before reporting a write as failed, re-read the cart and accept
  // it only when EVERY submitted attribute landed (an empty value means
  // removed). Checking the referral alone would let a real failure that also
  // carried the device token cache its signature and never retry (Codex
  // re-review, 2026-10-17).
  function cartMatches(attributes) {
    return fetch("/cart.js")
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (cart) {
        if (!cart || !cart.attributes) return false;
        return Object.keys(attributes).every(function (k) {
          return (cart.attributes[k] || "") === (attributes[k] || "");
        });
      })
      .catch(function () { return false; });
  }

  function readDeviceCookie() {
    try {
      var m = document.cookie.match(/(?:^|;\s*)galore_did=([a-f0-9]{32})(?:;|$)/);
      return m ? m[1] : null;
    } catch (e) { return null; }
  }

  // One durable id per browser. localStorage and the cookie back each other
  // up (clearing one but not the other is common); whichever survives wins,
  // and both are rewritten every load so the cookie's 400-day clock (the
  // Chrome maximum) keeps resetting while the customer keeps visiting.
  function deviceId() {
    var id = null;
    try {
      var stored = localStorage.getItem(DEVICE_KEY);
      if (stored && DEVICE_RE.test(stored)) id = stored;
    } catch (e) { /* storage blocked */ }
    if (!id) id = readDeviceCookie();
    if (!id) {
      // No weak Math.random fallback: a low-entropy id that COLLIDES with
      // another buyer is worse than no id, because in dispute evidence it
      // would falsely "match" a stranger's prior orders. If the platform has
      // no CSPRNG, mint nothing.
      if (!(window.crypto && crypto.getRandomValues)) return null;
      var bytes = new Uint8Array(16);
      crypto.getRandomValues(bytes);
      id = "";
      for (var j = 0; j < 16; j++) id += (bytes[j] + 256).toString(16).slice(1);
    }
    try { localStorage.setItem(DEVICE_KEY, id); } catch (e) { /* best-effort */ }
    try {
      document.cookie = DEVICE_COOKIE + "=" + id +
        "; Max-Age=34560000; Path=/; SameSite=Lax; Secure";
    } catch (e) { /* best-effort */ }
    return id;
  }

  // --- once per page load: capture a tracking link into storage ------------
  function capture() {
    var params = new URLSearchParams(window.location.search);

    var source = params.get("utm_source");
    if (source) {
      localStorage.setItem(ATTR_KEY, JSON.stringify({
        source: source,
        medium: params.get("utm_medium") || "",
        campaign: params.get("utm_campaign") || "",
        landing: (window.location.pathname + window.location.search).slice(0, 500),
        ts: Date.now(),
      }));
      localStorage.removeItem(SYNC_KEY);
    }

    var refToken = normaliseReferral(params.get("ref"));
    if (refToken) {
      // Last valid click wins over an earlier unconverted token.
      storeReferral(refToken);
      // Click beacon: rough click counts for support triage ("my friend
      // clicked but I got nothing") and funnel analytics. Fires only on
      // fresh capture, carries no visitor identity, and is never a
      // rewards input server-side.
      try {
        if (navigator.sendBeacon) {
          navigator.sendBeacon(
            "https://galore-rewards-api-240328775743.europe-west2.run.app/referral/click?token=" + refToken
          );
        }
      } catch (be) { /* best-effort */ }
      // Customer-shared referral links are the short form (no utm params);
      // the token itself is the attribution signal. Explicit utm_source on
      // the same URL wins (handled above, so only fill when absent).
      if (!source) {
        localStorage.setItem(ATTR_KEY, JSON.stringify({
          source: "referral",
          medium: "referral",
          campaign: "",
          landing: (window.location.pathname + window.location.search).slice(0, 500),
          ts: Date.now(),
        }));
      }
      localStorage.removeItem(SYNC_KEY);
    }
  }

  // --- the cart write, re-runnable --------------------------------------------
  // Runs on load (as before) and again on every cartUpdate event, because on
  // this Ajax-cart theme the common product -> add -> terms -> checkout path
  // never loads another page once the cart exists: a visitor who landed with
  // no cart and no tracking link reached checkout with no device token on
  // ~85% of orders (measured 2026-10-06). The event itself proves a cart, so
  // it bypasses the cookie gate. One write in flight at a time; a trigger
  // during a write runs it once more afterwards, and the signature cache
  // still makes an unchanged basket a no-op.
  var inFlight = null;
  var again = false;

  function syncCart(trusted) {
    if (inFlight) { again = true; return inFlight; }
    inFlight = Promise.resolve()
      .then(function () { return writeCart(trusted); })
      .catch(function () { return { ok: false }; })
      .then(function (result) {
        inFlight = null;
        if (again) { again = false; return syncCart(true); }
        return result || { ok: true };
      });
    return inFlight;
  }

  function writeCart(trusted) {
    var attribution = readFresh(ATTR_KEY);
    var referral = readFresh(REF_KEY) || (memoryReferral ? { token: memoryReferral } : null);

    // Sync whenever there is attribution/referral to write (the original
    // behaviour — do NOT regress it) OR a cart exists: on load that is the
    // `cart` cookie; from a cartUpdate event or an explicit sync() call the
    // caller vouches for it.
    var hasCart = trusted || /(?:^|;\s*)cart=/.test(document.cookie);
    if (!attribution && !referral && !hasCart && !pendingClear) return { ok: true };

    var device = deviceId();

    var attributes = {};
    if (device) {
      // Single underscore, matching _Galore Attribution / _Galore Referral:
      // these DO reach the order note_attributes (attribution_service reads
      // them in prod), whereas Shopify's double-underscore "private" cart
      // attributes are not guaranteed to persist onto the order — which is
      // exactly where this needs to land. The trade-off is that the name can
      // appear in checkout's additional-details; an opaque hex id is not
      // sensitive, so that's acceptable.
      attributes["_Galore Device"] = device;
    }
    if (attribution && attribution.source) {
      attributes["_Galore Attribution"] = JSON.stringify({
        source: attribution.source,
        medium: attribution.medium,
        campaign: attribution.campaign,
        landing: attribution.landing,
        touched_at: new Date(attribution.ts).toISOString(),
      });
    }
    if (referral && referral.token) {
      attributes["_Galore Referral"] = referral.token;
    }
    // device can be null (no CSPRNG); attribution/referral can both be absent
    // when we're here only because a cart already exists. Nothing to write.
    if (!Object.keys(attributes).length && !pendingClear) return { ok: true };

    return fetch("/cart.js")
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (cart) {
        if (!cart || !cart.token) return { ok: false };
        var clearing = false;
        if (!referral && pendingClear) {
          // Shopify keeps an omitted attribute; an empty string removes it.
          if (cart.attributes && cart.attributes["_Galore Referral"]) { attributes["_Galore Referral"] = ""; clearing = true; }
          else pendingClear = false;                       // nothing to clear
          if (!Object.keys(attributes).length) return { ok: true };
        }
        var sigAttrs = {};
        Object.keys(attributes).forEach(function (k) { if (attributes[k] !== "") sigAttrs[k] = attributes[k]; });
        var signature = cart.token + ":" + JSON.stringify(sigAttrs);
        var last = null;
        try { last = localStorage.getItem(SYNC_KEY); } catch (e) { /* storage blocked: write anyway */ }
        if (last === signature && !clearing) return { ok: true };
        var wantsReferral = Object.prototype.hasOwnProperty.call(attributes, "_Galore Referral");
        return fetch("/cart/update.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ attributes: attributes }),
        })
          .then(function (r) { return !!(r && r.ok); }, function () { return false; })
          .then(function (ok) { return ok ? true : cartMatches(attributes); })
          .then(function (ok) {
            if (!ok) return { ok: false };
            if (clearing) pendingClear = false;              // confirmed removal
            try { localStorage.setItem(SYNC_KEY, signature); } catch (e) { /* best-effort */ }
            if (wantsReferral) announceReferral(attributes["_Galore Referral"]);
            return { ok: true };
          });
      });
  }

  try { capture(); } catch (e) { /* storage blocked: still try the cart below */ }
  syncCart(false);
  try {
    if (typeof subscribe === "function" && typeof PUB_SUB_EVENTS !== "undefined" && PUB_SUB_EVENTS.cartUpdate) {
      subscribe(PUB_SUB_EVENTS.cartUpdate, function () { syncCart(true); });
    }
  } catch (e) { /* never break the storefront */ }
  // For the terms pop-up (app extension) to await before it redirects.
  window.GaloreAttribution = {
    sync: function () { return syncCart(true); },
    normalise: normaliseReferral,
    // The identifier this browser holds (a ?ref= landing or a typed code),
    // for the consent controller to carry as a line property on a
    // product-page express buy, where cart attributes never travel.
    current: function () {
      var r = readFresh(REF_KEY) || (memoryReferral ? { token: memoryReferral } : null);
      return r && r.token ? r.token : null;
    },
    // Cart referral-code field: a validated code (or token) replaces any
    // earlier referral, then the cart attribute is synced. Resolves false
    // for a malformed value without touching storage or the cart.
    setReferral: function (raw) {
      var value = normaliseReferral(raw);
      if (!value) return Promise.resolve(false);
      var previous = readFresh(REF_KEY) || (memoryReferral ? { token: memoryReferral } : null);
      var hadPendingClear = pendingClear;
      pendingClear = false;
      storeReferral(value);
      return syncCart(true).then(function (r) {
        if (r && r.ok) return true;
        // Not stored on the cart: put storage back so the two agree and the
        // field can honestly say "try again".
        if (previous && previous.token) storeReferral(previous.token); else forgetReferral();
        if (hadPendingClear) pendingClear = true;   // an unconfirmed clear is still owed
        return false;
      });
    },
    clearReferral: function () {
      forgetReferral();
      pendingClear = true;   // stays set until a write confirms removal
      return syncCart(true).then(function (r) { return !!(r && r.ok); });
    },
  };
})();
